import { Modal } from "@mui/material";
import {
  isStaleCartError,
  clearStaleCartStorage,
  showStaleCartDialog,
  isNoCartError,
  showNoCartDialog,
} from "../../../../utils/staleCartHandler";
import { useMutation } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import "./weighingModal.css";

import { AddItemThroughScanner, addRestaurantProducts } from "../../../../config/Services";
import { UserContext } from "../../../../App";
import useGetDevice from "./useGetDevice";
import { useDispatch } from "react-redux";
import { useSnackbar } from "./SnackbarProvider";
import { useAddToCart } from "./useAddToCart";
import { sortItems } from "../all-products/utils/cartUtils";
import {
  searchTextRes,
  searchTextRespaymnet,
  setNewOds,
  setOdsCart,
} from "../all-products/redux/Actions/paginationActions";
import { useNavigate } from "react-router-dom";

import {
  addManualItemsGTIN,
  addingProductTime,
  changePrevAddedTime,
  incrementManuallyAddedProduct,
  setTotalProducts,
} from "../all-products/redux/Actions/eventTracker";
import { useSelector } from "react-redux";

import {
  getManuallyAddedProduct,
  getTotalProducts,
} from "../all-products/redux/Reducers/eventTrackerReducer";
import { customEventTrigger } from "../../../../analytics";
import { setPort, setReader } from "../all-products/redux/Actions/weighinScaleActions";

/**
 * @typedef {Object} WeighingItemModal
 * @property {string} weighingStatus - The current status of the weighing operation. Possible values are 'init', 'scanning', and 'done'.
 * @property {function} findWeighingItem - A function that checks if an item is a weighing item and sets up the modal for it. It takes the GTIN, name, packaging type, and selling unit of the item as parameters.
 * @property {function} renderWeighingModal - A function that returns the JSX for the weighing item modal.
 */

/**
 * useWeighingItemModal is a custom hook that manages the state and functionality related to the weighing item modal.
 *
 * @returns {WeighingItemModal} The states and functions related to the weighing item modal.
 */

/**
 * @param {Object}   [options]
 * @param {function} [options.setAddItemThroughParticularCategoryItemLoader] - optional loader reset
 * @param {function} [options.onQuantityConfirm] - when provided, the modal only CAPTURES the
 *   quantity: on Add it calls onQuantityConfirm({ quantity, unit, gtin, name }) and closes,
 *   skipping all cart API calls (used by BundleBuilder to set a bundle item's quantity).
 */
const useWeighingItemModal = ({
  setAddItemThroughParticularCategoryItemLoader,
  onQuantityConfirm,
} = {}) => {
  const isMobile = useGetDevice();
  const dispatch = useDispatch();
  const showSnackbar = useSnackbar();
  const { addItemWithCheck } = useAddToCart();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [weighingStatus, setWeighingStatus] = useState("init");
  const [selectedGtin, setSelectedGtin] = useState("");
  const [itemName, setItemName] = useState("");
  const [quantity, setQuantity] = useState("");
  const [unit, setUnit] = useState("");
  const [rawSellingUnit, setRawSellingUnit] = useState("");
  const [cartIdOds, setCartIdOds] = useState("");
  const [listType, setListType] = useState("");
  const [couponApplied, setCouponApplied] = useState("0");
  const [appliedState, setAppliedState] = useState(false);
  const [variantId, setVariantId] = useState("");
  const { port, reader } = useSelector((state) => state.weighingScale);
  const [pendingWeighingCheck, setPendingWeighingCheck] = useState(false);
  /* Covers the non-restaurant add, which is not a react-query mutation */
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const {
    cartId,
    color,
    cartItems,
    setCartItems,
    setCgst,
    setSgst,
    setTotal,
    setSubTotal,
    setItemgtin,
    setanimationBox,
    setSearchText,
    setOfferFound,
    setCess,
  } = useContext(UserContext);
  const locId = localStorage.getItem("locId");
  const orgId = localStorage.getItem("orgId");
  const mannuallyEnteredProduct = useSelector(getManuallyAddedProduct);
  const totalProducts = useSelector(getTotalProducts);
  const [initialTime, setInitialTime] = useState();

  const styles = {
    modalStyle: {
      backgroundColor: color === "dark" ? "#353a50" : "#fff",
      color: color === "dark" ? "white" : "black",
      position: isMobile ? "static" : "fixed",
      top: "50%",
      left: "10%",
      maxWidth: isMobile ? "100%" : "40%",
      maxHeight: "100%",
      transform: isMobile ? "none" : "translate(50%, -50%)",
      width: isMobile ? "100%" : "50%",
      display: isMobile ? "flex" : "block",
      borderRadius: "10px",
      padding: "1.5rem",
      flexDirection: "column",
      justifyContent: "center",
      ...(isMobile && { height: "100%" }),
    },
    modalContent: {
      display: "flex",
      flexDirection: "column",
      gap: "1rem",
    },
    subTitle: {
      fontSize: "0.9rem",
      color: color === "dark" ? "white" : "#999",
    },
    modalFooter: {
      alignSelf: "flex-end",
      display: "flex",
      gap: "1rem",
    },
  };

  const retailType = localStorage.getItem("retailType");

  /* Clear the caller's add-button loader (only if the caller provided one) */
  const resetLoader = () => {
    if (setAddItemThroughParticularCategoryItemLoader) {
      setAddItemThroughParticularCategoryItemLoader(null);
    }
  };

  useEffect(() => {
    const scaleConfig = localStorage.getItem("scaleConfig");
    if (pendingWeighingCheck && unit === "gms" && scaleConfig) {
      setTimeout(() => checkWeight(), 500);
      setPendingWeighingCheck(false); // reset the flag
    }
  }, [unit, pendingWeighingCheck]);

  const checkWeight = async () => {
    try {
      setQuantity("");

      if (!("serial" in navigator)) {
        showSnackbar("Web Serial API is not supported in this browser.");
        return;
      }

      let ports = await navigator.serial.getPorts();
      let localPort = port;

      // If no port stored or none previously authorized, request one
      if (!localPort || ports.length === 0) {
        localPort = await navigator.serial.requestPort();
        await localPort.open({ baudRate: 9600 });
        dispatch(setPort(localPort));
      }

      // Flush stale data
      {
        const flushReader = localPort.readable.getReader();
        await new Promise((resolve) => setTimeout(resolve, 200));
        await flushReader.cancel();
        flushReader.releaseLock();
      }

      const localReader = localPort.readable.getReader();
      dispatch(setReader(localReader));

      const decoder = new TextDecoder();
      let buffer = "";
      let newWeight = null;
      const startTime = Date.now();

      while (Date.now() - startTime < 3000 && newWeight === null) {
        const { value, done } = await localReader.read();
        if (done) break;

        buffer += decoder.decode(value);

        if (buffer.includes("\r\n") || buffer.includes("\x03")) {
          const match = buffer.match(/-?\d+\.\d+/);
          const weight = match ? parseFloat(match[0]) : NaN;
          buffer = "";

          if (!isNaN(weight) && weight > 0) {
            newWeight = weight * 1000;
            setQuantity(newWeight.toFixed(0));
            break;
          }
        }
      }

      if (newWeight === null) {
        showSnackbar("No new weight reading received.");
      }

      await localReader.cancel();
      localReader.releaseLock();
      dispatch(setReader(null));
    } catch (error) {
      showSnackbar(`Error in checkWeight: ${error.message}`);
    }
  };

  const addWeighingItem = useMutation({
    mutationKey: "addWeighingItem",
    mutationFn: (data) => addRestaurantProducts(data),
    onSuccess: (res) => {
      if (res?.data?.data?.es) {
        const errMsg = res?.data?.data?.message || "";
        if (isNoCartError(errMsg)) {
          showNoCartDialog();
          return;
        }
        if (isStaleCartError(errMsg)) {
          clearStaleCartStorage();
          showStaleCartDialog();
          return;
        }
        showSnackbar(errMsg, "error");
        return;
      }
      if (listType === "search-ods") {
        dispatch(setNewOds(false));
        dispatch(setOdsCart(sortItems(res?.data?.data?.data?.cartProducts)));
      }
      setOfferFound(res?.data?.data?.data?.offerProductFound);
      setCartItems(sortItems(res?.data?.data?.data?.cartProducts));
      setCgst(res?.data?.data?.data?.billing?.cgst);
      setSgst(res?.data?.data?.data?.billing?.sgst);
      setCess(res.data.data.data.billing.totalCessAmount);
      setTotal(res?.data?.data?.data?.billing?.totalCartValue);
      setSubTotal(res?.data?.data?.data?.billing?.subtotal);
      setCouponApplied(res?.data?.data?.data?.billing?.appliedCoupon);
      setAppliedState(!appliedState);
      setanimationBox(true);
      setItemgtin(selectedGtin);
      dispatch(searchTextRespaymnet(""));
      dispatch(searchTextRes(""));
      const currentTime = new Date().getTime();
      dispatch(incrementManuallyAddedProduct(mannuallyEnteredProduct + 1));
      dispatch(setTotalProducts(totalProducts + 1));
      dispatch(changePrevAddedTime(currentTime));
      dispatch(addingProductTime(currentTime - initialTime));
      dispatch(addManualItemsGTIN(selectedGtin));
    },
    onError: (err) => {
      const errMsg = err?.response?.data?.message || err?.message || "";
      if (isStaleCartError(errMsg)) {
        clearStaleCartStorage();
        showStaleCartDialog();
        return;
      }
      showSnackbar(errMsg, "error");
    },
    onSettled: () => {
      setSearchText("");
      setQuantity("");
      setIsModalOpen(false);
      setWeighingStatus("done");
      resetLoader();
    },
  });

  const runNonRestaurantWeighingAdd = (payload) => {
    let handedOffToBatch = false;
    setIsSubmitting(true);
    addItemWithCheck(payload, {
      onBatchSelect: (dataArr) => {
        const batchItem = dataArr?.[0];
        if (batchItem) {
          handedOffToBatch = true;
          addMultipleBatchWeighingItem.mutate({
            /* Explicit cart id from the caller (ODS or bundle list) wins over context */
            cartId: cartIdOds || cartId,
            sellingPrice: batchItem.sellingPrice,
            gtin: selectedGtin,
            locationId: locId,
            mrp: batchItem.mrp,
            batchNo: batchItem.batchNo,
            purchasePrice: batchItem.purchasePrice,
            inventoryChecks: "NO",
            sellingUnit: batchItem.sellingUnit || null,
            quantityBySpecs: quantity || null,
          });
        }
      },
      onSuccess: (cartData) => {
        if (listType === "search-ods") {
          dispatch(setNewOds(false));
          dispatch(setOdsCart(sortItems(cartData?.cartProducts)));
        }
        setCouponApplied(cartData?.billing?.appliedCoupon);
        setAppliedState(!appliedState);
        setanimationBox(true);
        setItemgtin(selectedGtin);
        dispatch(searchTextRespaymnet(""));
        dispatch(searchTextRes(""));
        const currentTime = new Date().getTime();
        dispatch(incrementManuallyAddedProduct(mannuallyEnteredProduct + 1));
        dispatch(setTotalProducts(totalProducts + 1));
        dispatch(changePrevAddedTime(currentTime));
        dispatch(addingProductTime(currentTime - initialTime));
        dispatch(addManualItemsGTIN(selectedGtin));
      },
    }).finally(() => {
      setIsSubmitting(false);
      setSearchText("");
      setQuantity("");
      setIsModalOpen(false);
      setWeighingStatus("done");
      /* Batch path keeps the loader until addMultipleBatchWeighingItem settles */
      if (!handedOffToBatch) resetLoader();
    });
  };

  const addMultipleBatchWeighingItem = useMutation({
    mutationKey: "addMultipleBatchWeighingItem",
    mutationFn: (data) => AddItemThroughScanner(data),
    onSuccess: (res) => {
      if (res?.data?.data?.es === 1 || res?.data?.data?.es === 2) {
        const errMsg = res?.data?.data?.message || "";
        if (isStaleCartError(errMsg)) {
          clearStaleCartStorage();
          showStaleCartDialog();
          return;
        }
        if (isNoCartError(errMsg)) {
          showNoCartDialog();
          return;
        }
        showSnackbar(errMsg, "error");
        return;
      }
      if (listType === "search-ods") {
        dispatch(setNewOds(false));
        dispatch(setOdsCart(sortItems(res?.data?.data?.cartProducts)));
      }
      setOfferFound(res?.data?.data?.offerProductFound);
      setCartItems(sortItems(res?.data?.data?.cartProducts));
      setCgst(res?.data?.data?.billing?.cgst);
      setSgst(res?.data?.data?.billing?.sgst);
      setCess(res?.data?.data?.billing?.totalCessAmount);
      setTotal(res?.data?.data?.billing?.totalCartValue);
      setSubTotal(res?.data?.data?.billing?.subtotal);
      setCouponApplied(res?.data?.data?.billing?.appliedCoupon);
      setAppliedState(!appliedState);
      setanimationBox(true);
      setItemgtin(selectedGtin);
      dispatch(searchTextRespaymnet(""));
      dispatch(searchTextRes(""));
      const currentTime = new Date().getTime();
      // setMannuallyEnteredProduct(mannuallyEnteredProduct + 1);
      dispatch(incrementManuallyAddedProduct(mannuallyEnteredProduct + 1));
      dispatch(setTotalProducts(totalProducts + 1));
      dispatch(changePrevAddedTime(currentTime));
      dispatch(addingProductTime(currentTime - initialTime));
      dispatch(addManualItemsGTIN(selectedGtin));
    },
    onError: (err) => {
      const errMsg = err?.response?.data?.message || err?.message || "";
      if (isStaleCartError(errMsg)) {
        clearStaleCartStorage();
        showStaleCartDialog();
        return;
      }
      if (isNoCartError(errMsg)) {
        showNoCartDialog();
        return;
      }
      showSnackbar(errMsg, "error");
    },
    onSettled: () => {
      setSearchText("");
      setQuantity("");
      setIsModalOpen(false);
      setWeighingStatus("done");
      resetLoader();
    },
  });

  const checkSellingUnit = (sellingUnit) => {
    const modifiedSellingUnit = sellingUnit?.toLowerCase();
    const kgUnits = ["kg", "kgs", "kilograms", "kilogram"];
    const ltrUnits = ["ltr", "ltrs", "litres", "litre", "liter", "l"];
    const gmUnits = ["gm", "g", "gms", "grams", "grm", "gram"];
    const mlUnits = ["ml", "millilitres", "millilitre"];

    if (kgUnits.some((u) => modifiedSellingUnit.includes(u))) {
      setUnit("kg");
    } else if (ltrUnits.some((u) => modifiedSellingUnit.includes(u))) {
      setUnit("ml");
    } else if (gmUnits.some((u) => modifiedSellingUnit.includes(u))) {
      setUnit("gms");
    } else if (mlUnits.some((u) => modifiedSellingUnit.includes(u))) {
      setUnit("ml");
    } else {
      setUnit("nos");
    }
  };

  const findWeighingItem = (gtin, name, packagingType, sellingUnit, variant, odsCartId, type) => {
    if (
      packagingType?.toLowerCase() === "weighingscale" ||
      packagingType?.toLowerCase() === "weighing scale"
    ) {
      setQuantity("");
      setWeighingStatus("scanning");
      setIsModalOpen(true);
      setSelectedGtin(gtin);
      setItemName(name);
      checkSellingUnit(sellingUnit);
      setRawSellingUnit(sellingUnit || "");
      setCartIdOds(odsCartId);
      setListType(type);
      setVariantId(variant);
      setPendingWeighingCheck(true);

      return true;
    }
    return false;
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setWeighingStatus("done");
    //Use loader only if availabe
    if (setAddItemThroughParticularCategoryItemLoader) {
      setAddItemThroughParticularCategoryItemLoader(null);
    }
  };

  const handleAddClick = (e) => {
    e.preventDefault();
    if (isAdding) return; // Enter-key submit bypasses the disabled button

    setInitialTime(new Date().getTime());
    /* Capture-only mode: hand the quantity back to the caller, no cart API call */
    if (onQuantityConfirm) {
      const qtyNum = parseFloat(quantity);
      if (!Number.isFinite(qtyNum) || qtyNum <= 0) {
        showSnackbar("Enter a quantity greater than zero", "error");
        return;
      }
      /* Match the normal cart flow's input contract for kg/litre products:
         a decimal entry (e.g. "0.5") is already in the base unit (kg/ltr),
         while a whole-number entry (e.g. "500") is grams/ml and is divided
         by 1000 — so both reach the caller as 0.5. */
      const lowerUnit = rawSellingUnit?.trim().toLowerCase() || "";
      const kgUnits = ["kg", "kgs", "kilograms", "kilogram"];
      const ltrUnits = ["ltr", "ltrs", "litres", "litre", "liter"];
      const isBaseThousandUnit =
        kgUnits.some((u) => lowerUnit.includes(u)) ||
        ltrUnits.some((u) => lowerUnit.includes(u)) ||
        lowerUnit === "l";
      const enteredAsDecimal = String(quantity).includes(".");
      const finalQty = isBaseThousandUnit && !enteredAsDecimal ? qtyNum / 1000 : qtyNum;
      onQuantityConfirm({ quantity: finalQty, unit, gtin: selectedGtin, name: itemName });
      setQuantity("");
      setIsModalOpen(false);
      setWeighingStatus("done");
      if (setAddItemThroughParticularCategoryItemLoader) {
        setAddItemThroughParticularCategoryItemLoader(null);
      }
      return;
    }
    if (retailType === "RESTAURANT") {
      const restaurantPaload = {
        variantId: variantId || "",
        cartId: cartId,
        locationId: locId,
        orgId: orgId,
        quantityBySpecs: quantity,
      };
      customEventTrigger("pos_add_weighing_item_to_cart", {
        variantId: restaurantPaload.variantId,
        locationId: locId,
        orgId: orgId,
      });
      /* Loader is cleared in addWeighingItem.onSettled */
      addWeighingItem.mutate(restaurantPaload);
    } else {
      let qty = quantity;
      if (qty?.includes(".")) {
        qty = (parseFloat(qty) * 1000)?.toString();
      }
      const barcode = "W" + selectedGtin?.padStart(6, "0") + qty?.toString().padStart(6, "0");
      const payload = {
        gtin: barcode,
        /* Explicit cart id from the caller (ODS or bundle list) wins over context */
        cartId: cartIdOds || cartId,
        locationId: locId,
        orgId: orgId,
      };
      customEventTrigger("pos_add_weighing_item_to_cart", {
        gtin_value: payload?.gtin,
        locationId: locId,
        orgId: orgId,
      });
      /* Loader is cleared once the add request settles */
      runNonRestaurantWeighingAdd(payload);
    }
  };

  const scaleConfig = localStorage.getItem("scaleConfig");
  const isAdding =
    isSubmitting || addWeighingItem.isLoading || addMultipleBatchWeighingItem.isLoading;

  const renderWeighingModal = () => (
    <Modal open={isModalOpen} onClose={handleModalClose} disableAutoFocus>
      <div
        className={`wm-root${isMobile ? " wm-root--mobile" : ""}${color === "dark" ? " dark" : ""}`}
      >
        <div className="wm-modal">
          {/* ── Header ── */}
          <div className="wm-header">
            {/* Settings shortcut */}
            <button
              type="button"
              className="wm-settings-btn"
              onClick={() => navigate("/settings/peripherals/weighing-scale")}
              title="Scale settings"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </button>

            {/* Scale icon */}
            <div className="wm-icon-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="30"
                height="30"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="5" r="3" />
                <path d="M6.5 8a2 2 0 0 0-1.905 1.46L2.1 18.5A2 2 0 0 0 4 21h16a2 2 0 0 0 1.925-2.54L19.4 9.5A2 2 0 0 0 17.48 8Z" />
              </svg>
            </div>

            <h3 className="wm-title">Enter Quantity</h3>
            {itemName && <p className="wm-product-name">{itemName}</p>}
            {unit && <span className="wm-product-unit">{rawSellingUnit || unit}</span>}
          </div>

          {/* ── Input ── */}
          <div className="wm-body">
            <form id="wm-form" onSubmit={handleAddClick}>
              <div className="wm-input-wrap">
                <input
                  className="wm-input"
                  id="quantity-input"
                  type="number"
                  step="any"
                  min="0"
                  placeholder="0"
                  autoFocus
                  value={quantity}
                  onChange={(e) => {
                    if (unit === "nos" && e.target.value.includes(".")) return;
                    setQuantity(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "e" || e.key === "-" || e.key === "+") e.preventDefault();
                    if (unit === "nos" && e.key === ".") e.preventDefault();
                  }}
                />
                <div className="wm-unit-badge">{unit || "—"}</div>
              </div>
              {unit === "kg" && (
                <p className="wm-kg-hint">
                  <span className="wm-kg-hint-row">
                    <strong>0.5</strong>
                    <span className="wm-kg-hint-arrow">→</span>500 g{" "}
                    <span className="wm-kg-hint-label">(kg)</span>
                  </span>
                  <span className="wm-kg-hint-sep">or</span>
                  <span className="wm-kg-hint-row">
                    <strong>500</strong>
                    <span className="wm-kg-hint-arrow">→</span>500 g{" "}
                    <span className="wm-kg-hint-label">(grams)</span>
                  </span>
                </p>
              )}

              {scaleConfig && (
                <button type="button" className="wm-scan-btn" onClick={checkWeight}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M3 7V5a2 2 0 0 1 2-2h2" />
                    <path d="M17 3h2a2 2 0 0 1 2 2v2" />
                    <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
                    <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
                    <line x1="7" y1="12" x2="17" y2="12" />
                  </svg>
                  Read weight from scale
                </button>
              )}
            </form>
          </div>

          {/* ── Footer ── */}
          <div className="wm-footer">
            <button className="wm-btn wm-btn--cancel" type="button" onClick={handleModalClose}>
              Cancel
            </button>
            <button
              className="wm-btn wm-btn--add"
              type="submit"
              form="wm-form"
              disabled={!quantity || isAdding}
              onClick={handleAddClick}
            >
              {isAdding ? (
                <span className="wm-spinner" />
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 12h14" />
                  <path d="m12 5 7 7-7 7" />
                </svg>
              )}
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );

  return {
    weighingStatus,
    findWeighingItem,
    renderWeighingModal,
  };
};

export default useWeighingItemModal;
